import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/hooks/auth-provider'
import { useAuth } from '@/hooks/use-auth'
import { SiteBuilderAppShell as AppShell } from '@/components/layout/app-shell'
import { LoginPageWrapper as LoginPage } from '@/pages/login'
import { ProfilePageWrapper as ProfilePage } from '@/pages/profile'
import { DashboardPage } from '@/pages/dashboard'
import { SitesPage } from '@/pages/sites'
import { SiteCreatePage } from '@/pages/site-create'
import { SiteDetailPage } from '@/pages/site-detail'
import { CatalogTablePage } from '@/pages/catalog-table'
import { CatalogDetailPage } from '@/pages/catalog-detail'
import { Toaster } from '@/components/ui/sonner'
import type { ReactNode } from 'react'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen text-muted-foreground">Loading...</div>
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <div className="flex items-center justify-center min-h-screen text-muted-foreground">Loading...</div>
  if (user) return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="sites" element={<SitesPage />} />
              <Route path="sites/new" element={<SiteCreatePage />} />
              <Route path="sites/:id/*" element={<SiteDetailPage />} />
              <Route path="catalog/:table" element={<CatalogTablePage />} />
              <Route path="catalog/:table/:pk" element={<CatalogDetailPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </QueryClientProvider>
  )
}
